<template>
    <div class="filter_box flex flex_column"
         :class="{'with_select': schemeType}"
    >
        <div v-if="schemeType" class="base_margin">
            <div class="filter_header">
                <el-select
                    class="filter_select margin_r"
                    :class="{'nameRequired': nameRequiredError}"
                    :value="selectedFilter"
                    @change="selectFilter"
                    @clear="clearFilter(false)"
                    @focus="selectFilterFocus"
                    @blur="selectFilterBlur"
                    filterable
                    clearable
                    allow-create
                    default-first-option
                    :no-data-text="$t('common.select.noSearchResults')"
                    :placeholder="selectPlaceholderText"
                >
                    <el-option v-for="item in savedFilters"
                               :key="item.id"
                               :value="item.id"
                               :label="item.name"
                    ></el-option>
                </el-select>
                <div>
                    <md-button v-if="!activeFilterId" class="md-primary margin_r" @click="confirmSaveFilter">
                        {{$t("common.save")}}
                    </md-button>
                    <md-button v-else class="md-primary margin_r" @click="confirmEditFilter">
                        {{$t("common.save")}}
                    </md-button>
                    <md-button :class="{'md-primary': selectedFilter}" :disabled="!selectedFilter" @click="confirmDeleteFilter">
                        {{$t("common.delete")}}
                    </md-button>
                </div>
            </div>
        </div>

        <div class="filter_items flex margin_horizontal">
            <slot></slot>
        </div>

        <div class="filter_footer base_margin">
            <md-button class="md-blank margin_r" @click="clearFilter(false)">
                {{$t("common.clear")}}
            </md-button>
            <md-button class="md-primary" @click='applyFilter'>
                {{$t("common.apply")}}
            </md-button>
        </div>
    </div>
</template>

<script>
import {queryfilterApi} from "@/common/api/queryfilterApi";

export default {
    props: {
        schemeType: { type: [Number, null], default: null },
        filterRefs: { type: Object }
    },
    data() {
        return {
            selectedFilter: "",
            savedFilters: [],
            nameRequiredError: "",
        };
    },
    computed: {
        selectPlaceholderText() {
            return this.nameRequiredError || this.$t("common.filter.selectPlaceholder");
        },
        activeFilter() {
            return this.savedFilters.find(item => item.id === this.selectedFilter);
        },
        activeFilterId() {
            return this.activeFilter ? this.activeFilter.id : null;
        },
        activeFilterName() {
            return this.activeFilter ? this.activeFilter.name : this.selectedFilter;
        }
    },
    beforeMount() {
        if (this.schemeType) {
            this.loadSavedFilters();
        }
    },
    methods: {
        showNameRequiredError() {
            this.nameRequiredError = this.$t("filter.save.nameRequired");
        },
        clearNameRequiredError() {
            this.nameRequiredError = "";

            if (!this.activeFilter) {
                this.selectedFilter = "";
            }
        },
        selectFilter(value) {
            this.selectedFilter = value;

            if (this.activeFilter) {
                this.setFilter(this.activeFilter.scheme);
            }
        },
        confirmSaveFilter() {
            if (!this.selectedFilter) {
                this.showNameRequiredError();
                return;
            }

            let option = {
                title: this.$t("common.filters.save.confirm.title"),
                msg: this.$t("common.filters.save.confirm.msg", { filterName: this.activeFilterName }),
                confirmButtonText: this.$t("common.save"),
                confirmFun: this.saveFilter,
            };
            this.confirmMeg(option);
        },
        saveFilter() {
            const filterData = {
                name: this.selectedFilter,
                type: this.schemeType,
                filters: this.getFilter()
            };

            queryfilterApi.addFilterGroup(filterData)
                .then(res => {
                    this.toastSuccess();
                    this.loadSavedFilters()
                        .then(() => {
                            this.selectedFilter = res.msg;
                        });
                })
                .catch(e => {
                    this.toast(e);
                });
        },
        confirmEditFilter() {
            let option = {
                title: this.$t("common.filters.edit.confirm.title"),
                msg: this.$t("common.filters.edit.confirm.msg", { filterName: this.activeFilterName }),
                confirmButtonText: this.$t("common.save"),
                confirmFun: this.editFilter,
            };
            this.confirmMeg(option);
        },
        editFilter() {
            const filterData = {
                id: this.activeFilterId,
                name: this.activeFilterName,
                filters: this.getFilter()
            };

            queryfilterApi.editFilterGroup(filterData)
                .then(() => {
                    this.toastSuccess();
                    this.loadSavedFilters();
                })
                .catch(e => {
                    this.toast(e);
                });
        },
        confirmDeleteFilter() {
            let option = {
                title: this.$t("common.filters.delete.confirm.title"),
                msg: this.$t("common.filters.delete.confirm.msg", { filterName: this.activeFilterName }),
                confirmButtonText: this.$t("common.delete"),
                confirmFun: this.deleteFilter,
            };
            this.confirmMeg(option);
        },
        deleteFilter() {
            queryfilterApi.deleteFilterGroup(this.activeFilterId)
                .then(() => {
                    this.toastSuccess();
                    this.clearFilter(false);
                    this.loadSavedFilters();
                })
                .catch(e => {
                    this.toast(e);
                });
        },
        clearFilter(apply) {
            this.clearNameRequiredError();
            this.selectedFilter = "";

            for (const key in this.filterRefs) {
                let ref = this.filterRefs[key];
                if (!ref.clearFilter) {
                    continue;
                }

                ref.clearFilter();
            }

            if (apply) {
                this.applyFilter();
            }
        },
        applyFilter() {
            this.clearNameRequiredError();
            this.$emit("apply-filter", this.getFilter());
        },
        getFilter() {
            let postFilterData = [];

            for (const key in this.filterRefs) {
                let ref = this.filterRefs[key];
                if (!ref.getFilter) {
                    continue;
                }

                let filterData = ref.getFilter();
                if (!filterData) {
                    continue;
                }

                postFilterData = postFilterData.concat(filterData);
            }

            return postFilterData;
        },
        setFilter(filterData) {
            for (const key in this.filterRefs) {
                let ref = this.filterRefs[key];
                if (!ref.setFilter) {
                    continue;
                }

                ref.clearFilter();

                for (const filter of filterData) {
                    if (filter.columnName === ref.columnName) {
                        ref.setFilter(filter);
                    }
                }
            }
        },
        loadSavedFilters() {
            return new Promise((resolve, reject) => {
                queryfilterApi.getFilterGroup(this.schemeType)
                    .then(res => {
                        this.savedFilters = res.map(filter => {
                                return {
                                    id: filter.id,
                                    name: filter.name,
                                    scheme: JSON.parse(filter.scheme)
                                };
                            })
                            .sort((f1, f2) => {
                                return f1.name.localeCompare(f2.name);
                            });
                        resolve();
                    })
                    .catch(e => {
                        this.toast(e);
                        reject();
                    });
            });
        },
        selectFilterFocus() {
            this.clearNameRequiredError();

            // If we saved the selected filter using the workaround we need to reset it to keep
            // the selection behaviour consistent
            if (!this.activeFilter) {
                this.selectedFilter = "";
            }
        },
        selectFilterBlur(event) {
            // Usually the entered text is only saved on enter, so we need a workaround to keep entered filter name
            // if user does not press enter or selects his entered text, but just clicks on save directly
            this.selectedFilter = event.target.value;
        },
    },
};
</script>

<style lang="scss">
@import "@/assets/scss/base_variables.scss";

.filter_box {
    max-height: calc(100vh - 250px);
	min-height: 350px;
    border: 1px solid $table-border-color;
    background: #FFFFFF;
    position: absolute;
    right: 1rem;
    z-index: 100;

    .filter_header {
        display: flex;
        justify-content: flex-end;
        align-items: center;
    }

    .filter_footer {
        display: flex;
        justify-content: flex-end;
        align-items: center;
    }

    &.with_select {
        min-width: 600px;
        max-width: 100%;
    }

    .filter_select {
        width: 350px;

        &.nameRequired input {
            border: 1px solid $invalid-color !important;
        }
    }

    .filter_items {
        min-height: 200px;
        overflow: auto;

        .filter_item {
            width: 20rem;
            box-sizing: border-box;
            margin: 0 10px 10px 10px;

			&.filter_item_s {
				width: 18rem;
			}

            &.filter_item_l {
                width: 22rem;
            }

            &.filter_item_xl {
                width: 25rem;
            }

            &.filter_item_xxl {
                width: 27rem;
            }

            &.filter_item_stretch {
                width: 100%;
            }

            .el-input__inner input,
            .md-field label,
            .md-field input {
                padding-right: 0.5rem;
                padding-left: 0.5rem;
            }

            .el-field {
                &.el-field-range {
                    > label {
                        left: 0 !important;
                    }

                    .label-range-start {
                        left: 0.5rem !important;
                    }

                    .label-range-end {
                        margin: 0 !important;
                        padding-left: 0.5rem !important;
                    }
                }
            }

            .el-range-editor {
                padding: 0;

                .el-range__icon {
                    display: none;
                }

                input:nth-child(2) {
                    flex: none !important;
                    width: 50%;
                }

                input:nth-child(4) {
                    flex: 1;
                }
            }
        }

        .filter_head {
            background-color: $table-header-background-dark-color;
            padding: 0.5rem;
			margin-top: 10px;
        }
    }
}
</style>